import { useState, useEffect, useCallback, useRef } from 'react'
import { FileText, Loader2, AlertCircle, Clock, Cog } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { documentService } from '@/services/document'
import type { Document, DocumentPreviewResponse, DocumentStatus } from '@/schemas/document'

// Maximum number of chunks to fetch for preview
const MAX_PREVIEW_CHUNKS = 3

interface DocumentPreviewDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  kbId: string
  document: Document | null
}

export function DocumentPreviewDialog({
  open,
  onOpenChange,
  projectId,
  kbId,
  document,
}: DocumentPreviewDialogProps) {
  const docId = document?.id
  const filename = document?.filename ?? ''
  const [preview, setPreview] = useState<DocumentPreviewResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const fetchPreview = useCallback(async () => {
    if (!projectId || !kbId || !docId) return

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    const controller = new AbortController()
    abortControllerRef.current = controller

    setIsLoading(true)
    setError(null)

    try {
      const response = await documentService.getDocumentPreview(
        projectId,
        kbId,
        docId,
        MAX_PREVIEW_CHUNKS,
        { signal: controller.signal }
      )
      if (!controller.signal.aborted) {
        setPreview(response)
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        console.error('Failed to fetch document preview:', err)
        setError('Erro ao carregar preview')
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false)
      }
    }
  }, [projectId, kbId, docId])

  useEffect(() => {
    if (open && projectId && kbId && docId) {
      fetchPreview()
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [open, projectId, kbId, docId, fetchPreview])

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setPreview(null)
      setError(null)
    }
  }, [open])

  const renderStatusMessage = (status: DocumentStatus, errorMessage: string | null) => {
    if (status === 'pending') {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Clock className="w-12 h-12 text-yellow-400 mb-4" />
          <p className="text-gray-300 font-medium">Documento aguardando processamento</p>
          <p className="text-gray-500 text-sm mt-2">
            O conteudo estara disponivel apos o processamento
          </p>
        </div>
      )
    }

    if (status === 'processing') {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Cog className="w-12 h-12 text-blue-400 mb-4 animate-spin" />
          <p className="text-gray-300 font-medium">Processando documento...</p>
          <p className="text-gray-500 text-sm mt-2">
            Aguarde enquanto extraimos o conteudo
          </p>
        </div>
      )
    }

    if (status === 'error') {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
          <p className="text-gray-300 font-medium">Erro no processamento</p>
          {errorMessage && (
            <p className="text-red-400 text-sm mt-2 max-w-md">{errorMessage}</p>
          )}
        </div>
      )
    }

    return null
  }

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
        </div>
      )
    }

    if (error) {
      return (
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-400">{error}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchPreview}
            className="mt-4 border-gray-700/50 text-gray-300"
          >
            Tentar novamente
          </Button>
        </div>
      )
    }

    if (!preview) {
      return null
    }

    // Handle non-ready status
    if (preview.status !== 'ready') {
      return renderStatusMessage(preview.status, preview.error_message)
    }

    // Handle empty document (ready but no chunks)
    if (preview.chunks.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <FileText className="w-12 h-12 text-gray-500 mb-4" />
          <p className="text-gray-400">Documento sem conteudo</p>
          <p className="text-gray-500 text-sm mt-2">
            Nao foi possivel extrair texto deste documento
          </p>
        </div>
      )
    }

    // Render preview content
    return (
      <div className="space-y-4">
        {preview.chunks.map((chunk) => (
          <div
            key={chunk.chunk_index}
            className="bg-white/5 rounded-lg p-4 border border-gray-700/30"
          >
            <p className="text-gray-300 text-sm whitespace-pre-wrap leading-relaxed">
              {chunk.content}
            </p>
          </div>
        ))}

        {preview.has_more && (
          <p className="text-gray-500 text-sm text-center italic">
            ... e mais {preview.total_chunks - preview.chunks.length} chunks
          </p>
        )}
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-gray-700/50 sm:max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-400" />
            Preview do Documento
          </DialogTitle>
          <DialogDescription className="text-gray-400 truncate">
            {filename}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 pr-2">{renderContent()}</div>

        {preview && preview.status === 'ready' && preview.total_chunks > 0 && (
          <div className="pt-4 border-t border-gray-700/30 mt-4">
            <p className="text-sm text-gray-500">
              Mostrando {preview.chunks.length} de {preview.total_chunks} chunks
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
